import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Layout, message, Modal, Spin } from "antd";
import TopicSelector from "../../components/Shared/TopicSelector";
import SubtopicSelector from "../../components/Shared/SubtopicSelector";
import { useDispatch, useSelector } from "react-redux";
import { setTopics, setQuizzes, RootState } from "../../store";
import QuizCard from "../../components/Shared/QuizCard";
import EmptyState from "../../components/Shared/EmptyState";
import QuizGradients from "../../components/Shared/QuizGradients";

import { baseUrl } from "../../constants/api";
import showToastMessage from "../../utils/showToastMessage";
import DelBtn from "../../assets/svg/Delete";
import PreviewBtn from "../../assets/svg/PreviewBtn";

const QuizListAdmin = () => {
    const { topics } = useSelector((state: RootState) => state.quiz);
    const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
    const [selectedSubtopic, setSelectedSubtopic] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"regular" | "certification">("regular");
    const { quizzes } = useSelector((state: RootState) => state.quiz);
    const [loading, setLoading] = useState(false);
    const token = useSelector((state: RootState) => state.account.token);

    const [confirmationVisible, setConfirmationVisible] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState("");
    const [quizToDelete, setQuizToDelete] = useState<string | null>(null);
    const [quizid2, setQuizid2] = useState<string | null>(null);

    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewQuestions, setPreviewQuestions] = useState<any[]>([]);
    const [previewQuizName, setPreviewQuizName] = useState("");

    const dispatch = useDispatch();

    const openPreviewModal = async (quizId: string, quizName: string) => {
        setPreviewQuizName(quizName);
        setPreviewVisible(true);
        setPreviewLoading(true);
        try {
            const response = await fetch(`${baseUrl}/api/quiz/get-questions/${quizId}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setPreviewQuestions(data.questions || []);
            } else {
                const errorText = await response.text();
                message.error(`Failed to fetch questions: ${errorText}`);
                setPreviewVisible(false);
            }
        } catch (error) {
            message.error("An error occurred while fetching questions.");
            setPreviewVisible(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const fetchNoOfUsersGivenQuiz = async (quizId: string, quizName: string, quizid2: string) => {
        const apiUrl = `${baseUrl}/api/quiz/get-attempted-users/${quizId}`;
        setLoading(true);
        try {
            const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                const usersCount = data.data;

                if (usersCount === 0) {
                    setConfirmationMessage(
                        `No users have yet given the test. Are you sure you want to delete "${quizName}"?`
                    );
                } else {
                    setConfirmationMessage(
                        `${usersCount} users have already given the test. Are you sure you want to delete "${quizName}"?`
                    );
                }
                setQuizToDelete(quizId);
                setQuizid2(quizid2);
                setConfirmationVisible(true);
            } else {
                const errorText = await response.text();
                message.error(`Failed to fetch attempted users: ${errorText}`);
            }
        } catch (error) {
            message.error("An error occurred while fetching attempted users.");
        } finally {
            setLoading(false);
        }
    };

    const deleteQuiz = async () => {
        if (!quizToDelete) return;
        const apiUrl = `${baseUrl}/api/quiz/delete-quiz/${quizToDelete}?quizId=${quizid2}`;
        setLoading(true);
        try {
            const response = await fetch(apiUrl, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                if (selectedTopic && selectedSubtopic) {
                    fetchQuizzes(selectedTopic, selectedSubtopic);
                }
                message.success("Quiz deleted successfully !");
            } else {
                const errorText = await response.text();
                showToastMessage(
                    `Failed to delete quiz: ${errorText}`,
                    "error"
                );
            }
        } catch (error) {
            message.error("An error occurred while deleting the quiz.");
        } finally {
            setLoading(false);
            setConfirmationVisible(false);
        }
    };

    const fetchTopics = useCallback(async () => {
        const apiUrl = `${baseUrl}/api/topic/get-all-topics`;
        setLoading(true);
        try {
            const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const topicData = await response.json();
                dispatch(setTopics(topicData.data));
            } else {
                const errorText = await response.text();
                showToastMessage(
                    `Failed to fetch topics: ${errorText}`,
                    "error"
                );
            }
        } catch (error) {
            showToastMessage(
                "An error occurred while fetching topics.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    }, [token, dispatch]);

    const fetchQuizzes = useCallback(async (topicName: string, subtopicName: string) => {
        const apiUrl = `${baseUrl}/api/quiz/get-quizes?topic_name=${topicName}&subtopic_name=${subtopicName}`;
        setLoading(true);
        try {
            const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const quizData = await response.json();
                if (quizData?.quizes) {
                    dispatch(setQuizzes(quizData?.quizes));
                }
                else {
                    dispatch(setQuizzes([]));
                }
            } else {
                const errorText = await response.text();
                showToastMessage(
                    `Failed to fetch quizzes: ${errorText}`,
                    "error"
                );
            }
        } catch (error) {
            showToastMessage(
                "An error occurred while fetching quizzes.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    }, [token, dispatch]);

    useEffect(() => {
        fetchTopics();
    }, [fetchTopics]);

    useEffect(() => {
        if (topics && topics.length > 0 && !selectedTopic) {
            const firstTopic = topics[0].topic_name;
            const firstSubtopic = topics[0].subtopics && topics[0].subtopics.length > 0 ? topics[0].subtopics[0] : null;
            setSelectedTopic(firstTopic);
            setSelectedSubtopic(firstSubtopic);

            if (firstTopic && firstSubtopic) {
                fetchQuizzes(firstTopic, firstSubtopic);
            }
        }
    }, [topics, fetchQuizzes, selectedTopic]);

    const handleTopicSelect = (topic: string | null) => {
        setSelectedTopic(topic);
        setSelectedSubtopic(null);
        dispatch(setQuizzes([]));
    };

    const handleSubtopicSelect = (subtopic: string | null) => {
        setSelectedSubtopic(subtopic);
        if (selectedTopic && subtopic) {
            fetchQuizzes(selectedTopic, subtopic);
        }
    };

    const currentSubtopics = useMemo(() => {
        const topic = topics.find(t => t.topic_name === selectedTopic);
        return topic ? topic.subtopics : [];
    }, [topics, selectedTopic]);

    const visibleQuizzes = useMemo(() => {
        const quizList = Array.isArray(quizzes) ? quizzes : [];
        return quizList.filter((quiz: any) =>
            activeTab === "certification"
                ? quiz.quiz_type === "certification"
                : quiz.quiz_type !== "certification"
        );
    }, [quizzes, activeTab]);

    return (
        <Layout
            className="m-0 w-full flex flex-col flex-grow bg-transparent"
            style={{ minHeight: "calc(100vh - 200px)" }}
        >
            <QuizGradients />
            <div className="w-full">
                <>
                    <TopicSelector
                        topics={topics}
                        selectedTopic={selectedTopic}
                        handleTopicSelect={handleTopicSelect}
                    />
                    <div className="dashboard-mode-switch mt-3 mb-3">
                        <button
                            type="button"
                            className={`dashboard-mode-btn ${activeTab === "regular" ? "active" : ""}`}
                            onClick={() => setActiveTab("regular")}
                        >
                            Regular Quizzes
                        </button>
                        <button
                            type="button"
                            className={`dashboard-mode-btn ${activeTab === "certification" ? "active certification-tab-active" : ""}`}
                            onClick={() => setActiveTab("certification")}
                        >
                            Certification Quizzes
                        </button>
                    </div>
                    <div className="w-[98%] h-[1px] bg-[rgba(48,48,48,0.2)] mt-0 mb-2"></div>
                    <div className="w-[98%] h-[calc(100vh-250px)] bg-white rounded-lg flex flex-row border border-[rgba(48,48,48,0.1)] overflow-hidden">
                        <SubtopicSelector
                            subtopics={currentSubtopics}
                            selectedSubtopic={selectedSubtopic}
                            handleSubtopicSelect={handleSubtopicSelect}
                            isVertical={true}
                        />

                        <div className="flex-grow flex flex-col gap-4 p-6 overflow-y-auto">
                            {loading ? (
                                <div className="flex justify-center items-center py-10">
                                    <Spin size="large" />
                                </div>
                            ) : visibleQuizzes.length === 0 ? (
                                <EmptyState
                                    title={activeTab === "certification" ? "No Certification Quizzes" : "No Regular Quizzes"}
                                    message="Add quizzes from the Create Quiz section."
                                />
                            ) : (
                                visibleQuizzes.map((quiz: any, index: number) => (
                                    <QuizCard
                                        key={quiz.quiz_id}
                                        quiz={quiz}
                                        index={index}
                                        isAdmin={true}
                                        statusClass={quiz.enable_quiz ? "" : "unavailable"}
                                        actions={
                                            <div className="flex flex-row gap-2.5">
                                                <button
                                                    className="w-10 h-10 border-none rounded-md bg-[#FFF] flex items-center justify-center hover:bg-[#669bbc] hover:scale-95 transition-all border border-gray-100"
                                                    onClick={() => openPreviewModal(quiz.quiz_id, quiz.quiz_name)}
                                                    title="Preview Quiz"
                                                >
                                                    <PreviewBtn />
                                                </button>
                                                <button
                                                    className="w-10 h-10 border-none rounded-md bg-[#FFF] flex items-center justify-center hover:bg-[#669bbc] hover:scale-95 transition-all border border-gray-100"
                                                    onClick={() =>
                                                        fetchNoOfUsersGivenQuiz(
                                                            quiz.quiz_id,
                                                            quiz.quiz_name,
                                                            quiz._id
                                                        )
                                                    }
                                                    title="Delete Quiz"
                                                >
                                                    <DelBtn color={"#000"} />
                                                </button>
                                                {/* <button
                                                    className="w-10 h-10 border-none rounded-md bg-[#FFF] flex items-center justify-center hover:bg-[#669bbc] hover:scale-95 transition-all shadow-sm border border-gray-100"
                                                    onClick={() => updateQuiz(
                                                        quiz.quiz_id,
                                                        !quiz.enable_quiz
                                                    )}
                                                    title={quiz?.enable_quiz ? "Disable Quiz" : "Enable Quiz"}
                                                >
                                                    {quiz?.enable_quiz ? (
                                                        <VisibleBtn />
                                                    ) : (
                                                        <NotVisibleBtn />
                                                    )}
                                                </button> */}
                                            </div>
                                        }
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </>
            </div>
            <Modal
                confirmLoading={loading}
                open={confirmationVisible}
                onCancel={() => setConfirmationVisible(false)}
                onOk={deleteQuiz}
                okText="Confirm"
                cancelText="Cancel"
                centered
                bodyStyle={{
                    textAlign: "center" as const,
                    fontSize: "20px",
                    lineHeight: "1.6",
                    padding: "20px",
                    fontFamily: "Work Sans, sans-serif",
                }}
                title={
                    <span style={{ fontFamily: "Work Sans, sans-serif", fontSize: "20px", fontWeight: "bold" }}>
                        Confirmation
                    </span>
                }
                okButtonProps={{
                    style: {
                        backgroundColor: "#1890ff",
                        color: "#fff",
                        borderRadius: "4px",
                        fontSize: "20px",
                        padding: "10px 20px",
                    },
                }}
                cancelButtonProps={{
                    style: {
                        backgroundColor: "#f5f5f5",
                        color: "#000",
                        borderRadius: "4px",
                        fontSize: "20px",
                        padding: "10px 20px",
                    },
                }}
            >
                <p style={{ marginBottom: "10px", textAlign: "left" }}>{confirmationMessage}</p>
            </Modal>

            <Modal
                title={
                    <span style={{ fontFamily: "Work Sans, sans-serif", fontSize: "20px", fontWeight: "bold" }}>
                        Preview: {previewQuizName}
                    </span>
                }
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                width={900}
                centered
                bodyStyle={{
                    maxHeight: "75vh",
                    overflowY: "auto",
                    padding: "20px",
                    fontFamily: "Work Sans, sans-serif",
                }}
            >
                {previewLoading ? (
                    <div className="flex justify-center items-center py-10">
                        <Spin size="large" />
                    </div>
                ) : previewQuestions?.length === 0 ? (
                    <div className="text-center py-10 text-gray-500 text-lg">
                        No questions found for this quiz.
                    </div>
                ) : (
                    <div className="flex flex-col gap-6">
                        {previewQuestions?.map((q, idx) => (
                            <div key={q._id || idx} className="p-5 border border-gray-200 rounded-lg shadow-sm bg-white">
                                <div className="font-semibold text-lg mb-4 text-gray-800">
                                    {idx + 1}. {q.question}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {q.options?.map((opt: string, optIdx: number) => {
                                        const isCorrect = q.correct_option_id === optIdx;
                                        return (
                                            <div
                                                key={optIdx}
                                                className={`p-3 rounded-md border flex justify-between items-center ${isCorrect
                                                    ? "bg-green-50 border-green-500 text-green-900 font-medium shadow-sm"
                                                    : "bg-gray-50 border-gray-200 text-gray-700"
                                                    }`}
                                            >
                                                <span>
                                                    <span className="mr-2 font-bold">{String.fromCharCode(97 + optIdx)}.</span>
                                                    {opt}
                                                </span>
                                                {isCorrect && (
                                                    <span className="ml-2 inline-flex items-center justify-center bg-green-500 text-white rounded-full w-5 h-5 text-xs">
                                                        ✓
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal>
        </Layout>
    );
};

export default QuizListAdmin;
